import React from 'react';
import type { MarketProfileState } from './marketProfileStore';
import { computeProgress, isQuestionnaireLocked, isQuestionnaireSubmitted } from './marketProfileStore';
import { SHARE_QUESTIONNAIRE_LABEL } from './CombinedShareQuestionnaire';
import { resolveMarketProfileCta, type MarketProfileNavTarget } from './marketProfileCta';
import { ProgressBar, ReviewCountdown, SensitiveDataNotice, StatusPill } from './MarketProfileShared';
import MarketProfileScalePreview from './MarketProfileScalePreview';
import MarketDataDemoDashboard from './MarketDataDemoDashboard';
import { CommunityDevPath } from '../CommunityDevUi';

type Props = {
  state: MarketProfileState;
  onOpenProfile: () => void;
  onOpenShare: () => void;
  onOpenPosition: () => void;
  onOpenGrading: () => void;
  onOpenMarketData: () => void;
  onOpenPositionPending: () => void;
  onOpenGradingPending: () => void;
};

function formatUpdatedAt(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(d);
}

function openPrimaryTarget(target: MarketProfileNavTarget, handlers: {
  onOpenShare: () => void;
  onOpenPosition: () => void;
  onOpenGrading: () => void;
  onOpenMarketData: () => void;
}) {
  if (target === 'share') handlers.onOpenShare();
  else if (target === 'position') handlers.onOpenPosition();
  else if (target === 'grading') handlers.onOpenGrading();
  else if (target === 'marketData') handlers.onOpenMarketData();
}

const RESULT_ITEMS = [
  'релевантную отраслевую выборку',
  'точное позиционирование роли на базе её содержания',
  'детальный анализ вознаграждения в сравнении с рынком',
];

export default function ProfileOverview({
  state,
  onOpenProfile,
  onOpenShare,
  onOpenPosition,
  onOpenGrading,
  onOpenMarketData,
  onOpenPositionPending,
  onOpenGradingPending,
}: Props) {
  const progress = computeProgress(state);
  const cta = resolveMarketProfileCta(state);
  const positionUpdated = formatUpdatedAt(state.position.updatedAt);
  const gradingUpdated = formatUpdatedAt(state.grading.updatedAt);
  const profileUpdated = formatUpdatedAt(state.userProfile.updatedAt);
  const profileFilled = Boolean(state.userProfile.data.pseudonym.trim());

  const positionSubmitted = isQuestionnaireSubmitted(state.position.status);
  const gradingSubmitted = isQuestionnaireSubmitted(state.grading.status);
  const positionLocked = isQuestionnaireLocked(state.position.status);
  const gradingLocked = isQuestionnaireLocked(state.grading.status);
  const showHeroStepButtons = cta.kind !== 'complete' && cta.kind !== 'outdated';
  const positionPending = state.position.status === 'pending_review';
  const gradingPending = state.grading.status === 'pending_review';
  const anyPending = positionPending || gradingPending;

  const bothOpen = !positionLocked && !gradingLocked;
  const shareLabel = bothOpen
    ? (state.position.status === 'draft' || state.grading.status === 'draft'
      ? 'Продолжить анкету'
      : SHARE_QUESTIONNAIRE_LABEL)
    : !positionLocked
      ? (state.position.status === 'draft' ? 'Продолжить анкету' : 'Поделиться компенсационным пакетом')
      : !gradingLocked
        ? (state.grading.status === 'draft' ? 'Продолжить анкету' : 'Поделиться данными о функционале')
        : (anyPending ? 'На проверке' : 'Анкета отправлена');

  const handleShareClick = () => {
    if (bothOpen) {
      onOpenShare();
      return;
    }
    if (!positionLocked) {
      onOpenPosition();
      return;
    }
    if (!gradingLocked) {
      onOpenGrading();
      return;
    }
    if (positionPending) onOpenPositionPending();
    else if (gradingPending) onOpenGradingPending();
  };

  const handlePrimaryAction = () => {
    if (cta.primaryTarget === 'position' && positionLocked) return;
    if (cta.primaryTarget === 'grading' && gradingLocked) return;
    if (cta.primaryTarget === 'share' && !bothOpen) {
      handleShareClick();
      return;
    }
    openPrimaryTarget(cta.primaryTarget, {
      onOpenShare,
      onOpenPosition,
      onOpenGrading,
      onOpenMarketData,
    });
  };

  const primaryButtonLabel =
    cta.kind === 'complete' || cta.kind === 'outdated'
      ? (cta.kind === 'outdated' ? 'Обновить данные' : 'Перейти к рыночным данным')
      : 'Сравнить себя с рынком';

  const heroTitle =
    cta.kind === 'complete'
      ? 'Ваше сравнение с рынком готово'
      : cta.kind === 'outdated'
        ? 'Обновите рыночный профиль'
        : 'Узнайте, где вы находитесь относительно рынка';

  const heroDesc =
    cta.kind === 'complete'
      ? 'Компенсация, состав пакета и положение роли в грейдинговом дереве доступны в рыночных данных.'
      : cta.kind === 'outdated'
        ? 'Для актуального сравнения обновите данные о компенсации и при необходимости пройдите грейдинг заново.'
        : 'Пройдите опросник по вознаграждению и содержанию своей роли, чтобы узнать:';

  return (
    <div className="dcf-mp-overview">
      <section className={`dcf-mp-hero dcf-mp-hero--cta ${cta.showFillPrompt ? 'dcf-mp-hero--with-demo' : ''}`}>
        <div className="dcf-mp-hero__main">
          <p className="dcf-mp-hero__eyebrow">Мой профиль рынка</p>
          <h1 className="dcf-mp-hero__title">{heroTitle}</h1>
          <p className="dcf-mp-hero__desc">{heroDesc}</p>

          {cta.showFillPrompt && (
            <ul className="dcf-mp-hero__results">
              {RESULT_ITEMS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}

          <div className="dcf-mp-hero__actions">
            {showHeroStepButtons ? (
              positionSubmitted && gradingSubmitted ? (
                <span className="dcf-mp-hero__step-done">
                  <i className="fas fa-check" aria-hidden="true" />
                  Анкета отправлена
                </span>
              ) : (
                <button type="button" className="dcf-btn dcf-btn--primary dcf-mp-share-btn" onClick={handleShareClick}>
                  {shareLabel}
                </button>
              )
            ) : (
              <button type="button" className="dcf-btn dcf-btn--primary" onClick={handlePrimaryAction}>
                {primaryButtonLabel}
              </button>
            )}
          </div>

          <p className="dcf-mp-hero__privacy">
            Данные не отображаются в вашем профиле и используются только в обезличенной статистике.
          </p>
          <CommunityDevPath path="GET/PUT /private-circle/community/market-profile" />
        </div>

        {cta.showFillPrompt ? (
          <MarketDataDemoDashboard />
        ) : (
          <div className="dcf-mp-hero__aside">
            <MarketProfileScalePreview revealed={progress.completed === 2} />
            {progress.completed === 2 && (
              <span className={`dcf-mp-access-badge ${progress.unlocked ? 'dcf-mp-access-badge--open' : ''}`}>
                <i className={`fas ${progress.unlocked ? 'fa-circle-check' : 'fa-chart-line'}`} aria-hidden="true" />
                {progress.unlocked ? 'Данные открыты' : 'Готово к просмотру'}
              </span>
            )}
          </div>
        )}
      </section>

      {progress.outdated && (
        <div className="dcf-mp-notice dcf-mp-notice--outdated">
          <i className="fas fa-clock-rotate-left" aria-hidden="true" />
          <span>
            Данные о компенсации могли устареть. Для обновления профиля обратитесь к администратору сообщества.
          </span>
        </div>
      )}

      {positionPending && state.position.reviewSubmittedAt && (
        <div className="dcf-mp-notice dcf-mp-notice--review">
          <i className="fas fa-hourglass-half" aria-hidden="true" />
          <div className="dcf-mp-notice--review__body">
            <span>Данные о должности на проверке. Обычно это занимает до 24 часов.</span>
            <ReviewCountdown reviewSubmittedAt={state.position.reviewSubmittedAt} />
          </div>
        </div>
      )}

      {gradingPending && state.grading.reviewSubmittedAt && (
        <div className="dcf-mp-notice dcf-mp-notice--review">
          <i className="fas fa-hourglass-half" aria-hidden="true" />
          <div className="dcf-mp-notice--review__body">
            <span>Грейдинг на проверке. Обычно это занимает до 24 часов.</span>
            <ReviewCountdown reviewSubmittedAt={state.grading.reviewSubmittedAt} />
          </div>
        </div>
      )}

      <section className="dcf-mp-card dcf-mp-profile-summary">
        <div className="dcf-mp-action-card__head">
          <h2 className="dcf-mp-card__title">Профиль в сообществе</h2>
          {profileFilled ? (
            <span className="dcf-status-pill dcf-status-pill--active">Заполнен</span>
          ) : (
            <span className="dcf-status-pill dcf-status-pill--hidden">Не заполнен</span>
          )}
        </div>
        <p className="dcf-mp-action-card__desc">
          Укажите псевдоним и настройте, какие данные участники увидят рядом с вашими сообщениями.
        </p>
        {profileFilled && state.userProfile.data.pseudonym && (
          <p className="dcf-mp-profile-summary__pseudo">
            Псевдоним: <strong>{state.userProfile.data.pseudonym}</strong>
          </p>
        )}
        {profileUpdated && (
          <p className="dcf-mp-action-card__updated">Обновлено: {profileUpdated}</p>
        )}
        <button type="button" className="dcf-btn dcf-btn--secondary" onClick={onOpenProfile}>
          {profileFilled ? 'Редактировать профиль' : 'Заполнить профиль'}
        </button>
      </section>

      <section className="dcf-mp-card dcf-mp-progress-card">
        <div className="dcf-mp-card__head">
          <h2 className="dcf-mp-card__title">Прогресс заполнения</h2>
          <span className="dcf-mp-progress-count">{cta.progressLabel}</span>
        </div>
        <ProgressBar value={cta.progressPercent} />

        <div className="dcf-mp-steps">
          <div className="dcf-mp-step">
            <span className={`dcf-mp-step__check ${
              state.position.status === 'complete' ? 'dcf-mp-step__check--done'
                : positionPending ? 'dcf-mp-step__check--pending' : ''
            }`}>
              {state.position.status === 'complete' && <i className="fas fa-check" aria-hidden="true" />}
              {positionPending && <i className="fas fa-hourglass-half" aria-hidden="true" />}
            </span>
            <div className="dcf-mp-step__body">
              <span className="dcf-mp-step__name">Данные о должности и компенсации</span>
              <StatusPill status={state.position.status} />
            </div>
          </div>
          <div className="dcf-mp-step">
            <span className={`dcf-mp-step__check ${
              state.grading.status === 'complete' ? 'dcf-mp-step__check--done'
                : gradingPending ? 'dcf-mp-step__check--pending' : ''
            }`}>
              {state.grading.status === 'complete' && <i className="fas fa-check" aria-hidden="true" />}
              {gradingPending && <i className="fas fa-hourglass-half" aria-hidden="true" />}
            </span>
            <div className="dcf-mp-step__body">
              <span className="dcf-mp-step__name">Грейдинг роли</span>
              <StatusPill status={state.grading.status} />
            </div>
          </div>
        </div>

        <p className="dcf-mp-card__footnote">
          {progress.completed === 2
            ? 'Анкеты проверены. Откройте рыночные данные или обновите профиль при необходимости.'
            : anyPending
              ? 'Отправленные анкеты проверяются до 24 часов. После проверки вы сможете продолжить и открыть рыночные ориентиры.'
              : 'После заполнения вы получите доступ к рыночному сравнению компенсации и оценке реального уровня роли.'}
        </p>
        {progress.completed === 2 && (
          <button type="button" className="dcf-btn dcf-btn--primary" onClick={onOpenMarketData}>
            Перейти к рыночным данным
          </button>
        )}
        {cta.showFillPrompt && progress.completed < 2 && (
          <button type="button" className="dcf-btn dcf-btn--secondary" onClick={handlePrimaryAction}>
            {cta.kind === 'position_done' || cta.kind === 'grading_done'
              ? 'Завершить профиль'
              : 'Сравнить себя с рынком'}
          </button>
        )}
      </section>

      <p className="dcf-mp-questionnaires-privacy">
        Индивидуальные значения не видны другим участникам. Они используются для формирования агрегированных рыночных ориентиров.
      </p>

      <section className={`dcf-mp-card dcf-mp-action-card dcf-mp-share-card ${positionLocked && gradingLocked ? 'dcf-mp-action-card--locked' : ''}`}>
        <div className="dcf-mp-action-card__icon">
          <i className="fas fa-briefcase" aria-hidden="true" />
        </div>
        <div className="dcf-mp-action-card__head">
          <h3 className="dcf-mp-card__title">Компенсационный пакет и данные о функционале</h3>
        </div>
        <p className="dcf-mp-action-card__desc">
          Сначала вознаграждение, затем содержание роли. Оба блока идут одной анкетой
          и нужны, чтобы сравнить пакет с рынком.
        </p>
        <ul className="dcf-mp-action-card__list">
          <li>Должность, компания и сектор</li>
          <li>Оклад, STI и LTIP</li>
          <li>Направление и содержание роли</li>
          <li>Самостоятельность, влияние и команда</li>
        </ul>
        {(positionPending && state.position.reviewSubmittedAt) && (
          <ReviewCountdown reviewSubmittedAt={state.position.reviewSubmittedAt} />
        )}
        {(gradingPending && state.grading.reviewSubmittedAt && !positionPending) && (
          <ReviewCountdown reviewSubmittedAt={state.grading.reviewSubmittedAt} />
        )}
        {(positionUpdated || gradingUpdated) && (
          <p className="dcf-mp-action-card__updated">
            Обновлено: {positionUpdated || gradingUpdated}
          </p>
        )}
        <button
          type="button"
          className="dcf-btn dcf-btn--secondary dcf-mp-action-card__cta dcf-mp-share-btn"
          onClick={handleShareClick}
          disabled={positionLocked && gradingLocked && !anyPending}
        >
          {shareLabel}
        </button>
      </section>

      <SensitiveDataNotice />
    </div>
  );
}
